import moment from 'moment-timezone';

/**
 * Generates time intervals for a full 24-hour day based on format and interval.
 * @param {Object} options - Configuration options
 * @param {boolean} [options.is12Hour=true] - Use 12-hour format (true) or 24-hour (false)
 * @param {number} [options.interval=15] - Interval in minutes (default: 15)
 * @returns {Array} Array of objects with id, label, and value
 */
const generateTimeIntervals = ({ is12Hour = true, interval = 15 } = {}) => {
  const intervals = [];
  const totalMinutes = 24 * 60; // Full day in minutes

  for (let minutes = 0; minutes < totalMinutes; minutes += interval) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const period = is12Hour ? (hours < 12 ? 'AM' : 'PM') : '';
    const displayHours = is12Hour
      ? hours === 0
        ? 12
        : hours > 12
        ? hours - 12
        : hours
      : hours;
    const timeStr = `${displayHours.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}${is12Hour ? ` ${period}` : ''}`;

    intervals.push({
      id: intervals.length + 1,
      label: timeStr,
      value: timeStr,
    });
  }

  return intervals;
};

/**
 * Generates duration intervals up to a maximum duration in hours.
 * @param {Object} options - Configuration options
 * @param {number} [options.maxDuration=24] - Maximum duration in hours (default: 24)
 * @param {number} [options.interval=15] - Interval in minutes (default: 15)
 * @returns {Array} Array of objects with label and value
 */
const generateDurationIntervals = ({
  maxDuration = 24, // Default to 24 hours
  interval = 15, // Default interval in minutes
} = {}) => {
  const intervals = [];
  const maxMinutes = maxDuration * 60; // Convert hours to minutes

  for (let minutes = interval; minutes <= maxMinutes; minutes += interval) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    let label = '';

    if (hours === 0) label = `${mins} min`;
    else if (mins === 0) label = `${hours} hour${hours > 1 ? 's' : ''}`;
    else label = `${hours} hour${hours > 1 ? 's' : ''} ${mins} min`;

    intervals.push({ label, value: minutes });
  }

  return intervals;
};

// Helper function to format date and time into ISO string with better validation
// we will get date like 2024-10-19 and time like 09:00 AM and timezone like America/New_York
const getIsoString = (dateStr, timeStr, timezone, dateFormat) => {
  if (!dateStr || !timeStr) return null;

  try {
    // Parse time string, handling both 12-hour and 24-hour formats
    // Check if timeStr includes 'AM' or 'PM' to determine format
    const is12HourFormat = timeStr.includes('AM') || timeStr.includes('PM');
    const timeFormat = is12HourFormat ? 'hh:mm A' : 'HH:mm';

    // First create a moment object just to parse the time
    const timeMoment = moment(timeStr, timeFormat);

    if (!timeMoment.isValid()) {
      console.warn('Invalid time format', timeStr);
      return null;
    }

    // Extract hours and minutes from parsed time
    const hour = timeMoment.hour();
    const minute = timeMoment.minute();

    // Create a new moment in the target timezone, starting with the date
    const dateMoment = moment.tz(dateStr, dateFormat, timezone);

    if (!dateMoment.isValid()) {
      console.warn('Invalid date format', dateStr);
      return null;
    }

    // Set the time components on the date
    const formattedDate = dateMoment.clone().set({
      hour: hour,
      minute: minute,
      second: 0,
      millisecond: 0,
    });

    // Verify the result is valid before returning
    if (!formattedDate.isValid()) {
      console.warn('Invalid combined date/time', dateStr, timeStr);
      return null;
    }

    return formattedDate.toISOString();
  } catch (error) {
    console.error('Error formatting date/time', error);
    return null;
  }
};

// Helper function to get time difference in minutes with validation
const getDurationInMinutes = (startIso, endIso, timezone) => {
  if (!startIso || !endIso) return 30; // Default duration

  try {
    const start = moment.tz(startIso, timezone);
    const end = moment.tz(endIso, timezone);

    if (!start.isValid() || !end.isValid()) {
      console.warn('Invalid date in duration calculation', { start, end });
      return 30;
    }

    // Ensure end is after start, if not, assume next day
    if (end.isBefore(start)) {
      return end.add(1, 'day').diff(start, 'minutes');
    }

    return end.diff(start, 'minutes');
  } catch (error) {
    console.error('Error calculating duration', error);
    return 30;
  }
};

// Helper function to navigate FullCalendar to the week containing a date
const navigateToWeek = (dateStr, timezone, calendarRef, attempt = 0) => {
  const currentCalendarApi = calendarRef.current?.getApi();
  if (!currentCalendarApi) {
    if (attempt < 3) {
      setTimeout(
        () =>
          navigateToWeek(dateStr, timezone, currentCalendarApi, attempt + 1),
        100
      );
    }
    return;
  }
  try {
    const date = moment.tz(dateStr, timezone);
    if (!date.isValid()) {
      console.warn('Invalid date for calendar navigation', dateStr);
      return;
    }

    const originalTime = date.format('HH:mm');
    const hours = parseInt(date.format('HH'), 10);
    let adjustedTime = originalTime;
    if (hours > 2 && hours < 22) {
      const adjustedMoment = date.clone().subtract(2, 'hours');
      adjustedTime = adjustedMoment.format('HH:mm');
    }

    // Ensure API still exists (paranoid check)
    if (!currentCalendarApi) {
      return;
    }

    currentCalendarApi.gotoDate(date.toDate());

    // Introduce a small delay before scrolling
    setTimeout(() => {
      try {
        // Check API again inside timeout, as component could unmount
        const currentCalendarApi = calendarRef.current?.getApi(); // Re-fetch or ensure calendarApi is stable
        if (currentCalendarApi) {
          currentCalendarApi.scrollToTime(adjustedTime);
        }
      } catch (scrollError) {
        console.error('Error during delayed scrollToTime:', scrollError);
      }
    }, 100); // Delay in milliseconds (adjust 50-200 if needed)
  } catch (error) {
    console.error('Error navigating to week:', error);
  }
};

// Helper function to get or set default time
const getDefaultTime = () => '09:00 AM';

// Helper function to find the closest duration from available options
const findClosestDuration = (durationMinutes, durationIntervals) => {
  if (!durationMinutes || durationMinutes < 0) return 30; // Default for invalid input

  // Find the closest duration option
  const availableDurations = durationIntervals.map((option) =>
    parseInt(option.value, 10)
  );
  return availableDurations.reduce((prev, curr) => {
    return Math.abs(curr - durationMinutes) < Math.abs(prev - durationMinutes)
      ? curr
      : prev;
  }, availableDurations[0] || 30);
};

// Helper function to find the closest time from available options
const findClosestTime = (timeString, options) => {
  if (!timeString) return getDefaultTime();

  try {
    const targetTime = moment(timeString, 'hh:mm A');
    if (!targetTime.isValid()) return getDefaultTime();

    const targetMinutes = targetTime.hour() * 60 + targetTime.minute();

    let closestTime = getDefaultTime();
    let minDiff = Infinity;

    options.forEach((interval) => {
      const time = moment(interval.value, 'hh:mm A');
      if (!time.isValid()) return;

      const minutes = time.hour() * 60 + time.minute();
      const diff = Math.abs(minutes - targetMinutes);

      if (diff < minDiff) {
        minDiff = diff;
        closestTime = interval.value;
      }
    });

    return closestTime;
  } catch (error) {
    console.error('Error finding closest time', error);
    return getDefaultTime();
  }
};

/**
 * Normalizes and formats time input strings into "h:mm A" format (e.g., "2:30 PM").
 * Handles formats like "11:30", "11.30", "1130", "14:30", "11:6pm", etc.
 * Returns null if the input is invalid.
 *
 * @param {string} input - The raw time string entered by the user
 * @param {React.MutableRefObject} formRef - Reference to the form for context (e.g., startTime)
 * @returns {string|null} - Formatted time string (e.g., "2:30 PM") or null if invalid
 */
const normalizeTimeInput = (input, formRef) => {
  if (!input || typeof input !== 'string') return null;

  // Step 1: Clean and normalize the input
  let normalizedInput = input.trim().toLowerCase();

  // Step 2: Extract AM/PM indicator
  let period = '';
  if (normalizedInput.endsWith('am')) {
    period = 'AM';
    normalizedInput = normalizedInput.slice(0, -2).trim();
  } else if (normalizedInput.endsWith('pm')) {
    period = 'PM';
    normalizedInput = normalizedInput.slice(0, -2).trim();
  }

  // Step 3: Replace separators with colons and clean up
  normalizedInput = normalizedInput
    .replace(/[.\s]+/g, ':') // Replace dots or spaces with colons
    .replace(/:+/g, ':') // Ensure single colons
    .replace(/^:+|:+$/g, ''); // Remove leading/trailing colons

  // Step 4: Parse hours and minutes based on format
  let hours = 0;
  let minutes = 0;

  // Handle formats with colons (e.g., "11:30", "2:5")
  if (normalizedInput.includes(':')) {
    const parts = normalizedInput.split(':');
    if (parts.length !== 2) return null;

    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10);
  }
  // Handle formats without colons (e.g., "1130", "230", "14")
  else {
    const num = parseInt(normalizedInput, 10);
    if (isNaN(num)) return null;

    if (normalizedInput.length <= 2) {
      // Single or double digit (hours only): "1", "11"
      hours = num;
      minutes = 0;
    } else if (normalizedInput.length === 3) {
      // Three digits: "130" = 1:30
      hours = parseInt(normalizedInput.substring(0, 1), 10);
      minutes = parseInt(normalizedInput.substring(1), 10);
    } else if (normalizedInput.length === 4) {
      // Four digits: "1130" = 11:30
      hours = parseInt(normalizedInput.substring(0, 2), 10);
      minutes = parseInt(normalizedInput.substring(2), 10);
    } else {
      return null; // Invalid format
    }
  }

  // Step 5: Validate and convert hours for 24-hour format
  if (hours > 12 && hours <= 23) {
    period = 'PM';
    hours -= 12;
  } else if (hours === 0) {
    hours = 12;
    period = 'AM';
  } else if (hours > 23) {
    return null; // Invalid hours
  }

  // Validate hours and minutes
  if (isNaN(hours) || hours < 1 || hours > 12) return null;
  if (isNaN(minutes) || minutes < 0 || minutes > 59) return null;

  // Step 6: Infer AM/PM if not specified
  if (!period) {
    const { values } = formRef.current || {};
    const { startTime } = values || {};
    if (startTime) {
      const startMoment = moment(startTime, 'hh:mm A');
      if (startMoment.isValid()) {
        const startHour = startMoment.hour(); // 0-23
        period = startHour >= 12 ? 'PM' : 'AM';
      }
    }
    // Default to AM if no context
    if (!period) {
      const currentHour = new Date().getHours();
      period = currentHour >= 12 ? 'PM' : 'AM';
    }
  }

  // Step 7: Format the final time
  return `${hours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

export const SchDurationTimeFieldsHelpers = {
  generateDurationIntervals,
  generateTimeIntervals,
  getIsoString,
  findClosestTime,
  getDurationInMinutes,
  navigateToWeek,
  findClosestDuration,
  normalizeTimeInput,
};
